import {
  createContext,
  use,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  confirmSignUp as amplifyConfirmSignUp,
  fetchAuthSession,
  getCurrentUser,
  resendSignUpCode,
  signIn as amplifySignIn,
  signOut as amplifySignOut,
  signUp as amplifySignUp,
  type AuthUser,
} from "aws-amplify/auth";
import { Hub } from "aws-amplify/utils";

/**
 * Session state for the signed-in Pro Portal (build-plan unit 15). Mirrors
 * customer/web/src/auth/AuthProvider.tsx — the systems deliberately do not
 * share UI code, but the auth integration patterns are kept identical so
 * fixes port across by inspection.
 *
 * The magic-link flow (routes/Opportunity.tsx) does NOT use this: it
 * authenticates per-request with the invitation token and must keep working
 * for signed-out contractors.
 */

/**
 * Thrown by Amplify v6's `signIn` when a Cognito session already exists in
 * storage for this origin. Exported so the sign-in form can render a
 * friendly fallback if it ever races past our recovery in `signIn` below.
 */
export const USER_ALREADY_AUTHENTICATED = "UserAlreadyAuthenticatedException";

/**
 * Amplify persists Cognito tokens in `localStorage` under keys prefixed with
 * this string. We use it to filter cross-tab `storage` events so we only
 * refresh auth state when the *auth* storage actually changed.
 */
const COGNITO_STORAGE_PREFIX = "CognitoIdentityServiceProvider.";

export interface AuthState {
  status: "loading" | "signed-out" | "signed-in";
  user: AuthUser | null;
  signIn: (input: { email: string; password: string }) => Promise<void>;
  signUp: (input: {
    email: string;
    password: string;
    givenName?: string;
    familyName?: string;
  }) => Promise<{ needsConfirmation: boolean }>;
  confirmSignUp: (input: { email: string; code: string }) => Promise<void>;
  resendCode: (input: { email: string }) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [status, setStatus] = useState<AuthState["status"]>("loading");

  const refresh = useCallback(async () => {
    try {
      const current = await getCurrentUser();
      setUser(current);
      setStatus("signed-in");
    } catch {
      setUser(null);
      setStatus("signed-out");
    }
  }, []);

  // Restore session on mount and keep it in sync across tabs.
  //
  // Two listeners cover the two failure modes for "tab A signs in/out, tab B
  // is stale":
  //   1. Amplify `Hub` — fires for in-tab auth events (sign-in, sign-out,
  //      token refresh). Catches programmatic state changes Amplify makes.
  //   2. `storage` event — fires in *other* tabs when localStorage changes.
  //      Amplify writes Cognito tokens to localStorage, so when tab A signs
  //      in or out, tab B sees a `storage` event for a Cognito-prefixed key
  //      and we re-pull the current user.
  useEffect(() => {
    void refresh();

    const unsubscribeHub = Hub.listen("auth", ({ payload }) => {
      if (
        payload.event === "signedIn" ||
        payload.event === "signedOut" ||
        payload.event === "tokenRefresh" ||
        payload.event === "tokenRefresh_failure"
      ) {
        void refresh();
      }
    });

    function onStorage(e: StorageEvent) {
      // `key === null` happens on `localStorage.clear()` — treat as auth change.
      if (e.key === null || e.key.startsWith(COGNITO_STORAGE_PREFIX)) {
        void refresh();
      }
    }
    window.addEventListener("storage", onStorage);

    return () => {
      unsubscribeHub();
      window.removeEventListener("storage", onStorage);
    };
  }, [refresh]);

  const value = useMemo<AuthState>(
    () => ({
      status,
      user,
      async signIn({ email, password }) {
        // Amplify v6 throws `UserAlreadyAuthenticatedException` if a session
        // already exists in storage — common when the user opens `/sign-in`
        // in a second tab, or comes back to a stale tab. Rather than dump
        // the raw error onto the form, recover:
        //   - same user → just refresh state so the caller can navigate.
        //   - different user → sign out the existing session and retry once,
        //     so "switch accounts" is a one-click flow.
        const attempt = () => amplifySignIn({ username: email, password });
        let result;
        try {
          result = await attempt();
        } catch (err) {
          if (err instanceof Error && err.name === USER_ALREADY_AUTHENTICATED) {
            const existing = await getCurrentUser().catch(() => null);
            const existingEmail = existing?.signInDetails?.loginId ?? existing?.username ?? "";
            if (existingEmail.toLowerCase() === email.toLowerCase()) {
              await refresh();
              return;
            }
            await amplifySignOut();
            result = await attempt();
          } else {
            throw err;
          }
        }
        if (result.isSignedIn) {
          await refresh();
        } else {
          // Other steps (MFA, force password change) aren't enabled.
          throw new Error(`Unexpected sign-in step: ${result.nextStep.signInStep}`);
        }
      },
      async signUp({ email, password, givenName, familyName }) {
        // Only the person's identity goes to Cognito. Business profile data
        // (company, phone, categories, service area) belongs to the registry
        // via the unit-16 application/claiming flows.
        const result = await amplifySignUp({
          username: email,
          password,
          options: {
            userAttributes: {
              email,
              ...(givenName ? { given_name: givenName } : {}),
              ...(familyName ? { family_name: familyName } : {}),
            },
          },
        });
        return {
          needsConfirmation: result.nextStep.signUpStep === "CONFIRM_SIGN_UP",
        };
      },
      async confirmSignUp({ email, code }) {
        await amplifyConfirmSignUp({ username: email, confirmationCode: code });
      },
      async resendCode({ email }) {
        await resendSignUpCode({ username: email });
      },
      async signOut() {
        await amplifySignOut();
        setUser(null);
        setStatus("signed-out");
      },
    }),
    [status, user, refresh],
  );

  return <AuthContext value={value}>{children}</AuthContext>;
}

export function useAuth(): AuthState {
  const ctx = use(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used inside <AuthProvider>");
  }
  return ctx;
}

/**
 * Like useAuth, but tolerates a missing provider (returns null). For
 * surfaces that merely ADAPT to a session — the magic-link page works
 * signed-out by design and must not require auth context to render.
 */
export function useOptionalAuth(): AuthState | null {
  return use(AuthContext);
}

/**
 * Returns the current Cognito ID token (or null when signed out).
 * Used by portal/api.ts to attach `Authorization: Bearer <id-token>` —
 * the JWT authorizer's audience is the SPA client id, which only the ID
 * token carries.
 */
export async function getIdToken(): Promise<string | null> {
  try {
    const session = await fetchAuthSession();
    return session.tokens?.idToken?.toString() ?? null;
  } catch {
    return null;
  }
}
